import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import type { NextRouter } from 'next/router';

import { ORDER_TYPE_DELIVERY } from 'shared/constants';
import { formatAddress } from 'shared/helpers/address';
import { addDispensaryRoutes, buildBaseRoute } from 'shared/helpers/route-helpers';
import useCart from 'hooks/use-cart';
import useSearchPreferences from 'hooks/use-search-preferences';
import useUI from 'hooks/use-ui';
import { GetStoreRoutingCandidatesDocument } from 'src/gql/graphql';
import type { Dispensaries, GetStoreRoutingCandidatesQuery } from 'src/gql/graphql';
import { STORE_ROUTING_FLAG } from 'src/constants/store-routing';
import type { StoreRoutingOutcome } from 'src/constants/store-routing';
import type { CurrentAddress } from 'src/state/ui';
import {
  clearCartForStoreRouting,
  getStoreRoutingAttemptFingerprint,
  getStoreRoutingCartTransitionState,
} from 'src/state/actions/cart';
import {
  StoreRoutingOfferData,
  StoreRoutingOfferResult,
  useStoreRoutingOfferModal,
} from 'components/modals/store-routing-offer';

type CurrentDispensary = Pick<Dispensaries, 'chain' | 'cName' | 'id' | 'name'>;

type OfferStoreRouteInput = {
  address: CurrentAddress;
  currentDispensary: CurrentDispensary;
  isStillEligible?: () => boolean;
};

type RoutableAddress = CurrentAddress & { lat: number; lng: number };

type StoreRoutingCandidate = NonNullable<NonNullable<GetStoreRoutingCandidatesQuery['filteredDispensaries']>[number]>;

type ActiveAttempt = {
  cancellation: 'aborted' | 'superseded' | null;
  fingerprint: string;
  participants: Map<symbol, () => boolean>;
  promise: Promise<StoreRoutingOutcome>;
  stage: 'lookup' | 'modal' | 'transition';
};

type AwaitModalResult = StoreRoutingOfferResult | 'superseded';

let activeAttempt: ActiveAttempt | null = null;

const hasRequiredLookupInput = (address: CurrentAddress, dispensary: CurrentDispensary): address is RoutableAddress =>
  Boolean(
    dispensary.id &&
      dispensary.name &&
      dispensary.chain &&
      address.ln1?.trim() &&
      address.city?.trim() &&
      address.state?.trim() &&
      address.zipcode?.trim() &&
      address.lat !== null &&
      address.lat !== undefined &&
      address.lng !== null &&
      address.lng !== undefined
  );

export const createDeferred = <T>(): { promise: Promise<T>; resolve: (value: T) => void } => {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((resolvePromise) => {
    resolve = resolvePromise;
  });
  return { promise, resolve };
};

async function awaitModalResult(
  openModal: (data: StoreRoutingOfferData) => Promise<StoreRoutingOfferResult>,
  data: StoreRoutingOfferData,
  router: NextRouter
): Promise<AwaitModalResult> {
  const { promise: routeChanged, resolve } = createDeferred<AwaitModalResult>();
  const handleRouteComplete = (): void => resolve('superseded');
  const handleRouteError = (): void => resolve('declined');
  router.events.on('routeChangeComplete', handleRouteComplete);
  router.events.on('routeChangeError', handleRouteError);

  try {
    return await Promise.race([openModal(data), routeChanged]);
  } finally {
    router.events.off('routeChangeComplete', handleRouteComplete);
    router.events.off('routeChangeError', handleRouteError);
  }
}

export function useStoreRoutingOffer(): (input: OfferStoreRouteInput) => Promise<StoreRoutingOutcome> {
  const flags = useFlags();
  const flagsRef = useRef(flags);
  const owner = useMemo(() => Symbol('store-routing-owner'), []);
  const mountedRef = useRef(true);
  const [queryCandidates] = useLazyQuery(GetStoreRoutingCandidatesDocument);
  const { open: openModal } = useStoreRoutingOfferModal();
  const Cart = useCart();
  const UI = useUI();
  const SearchPreferences = useSearchPreferences();
  const router = useRouter();

  // Keep callbacks stable while eligibility follows the latest flag value.
  flagsRef.current = flags;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const attempt = activeAttempt;
      attempt?.participants.delete(owner);
      if (attempt && activeAttempt === attempt && attempt.participants.size === 0) {
        attempt.cancellation = 'aborted';
      }
    };
  }, [owner]);
  return useCallback(
    ({ address, currentDispensary, isStillEligible }: OfferStoreRouteInput): Promise<StoreRoutingOutcome> => {
      if (
        !router ||
        !flagsRef.current[STORE_ROUTING_FLAG] ||
        UI.isDemo ||
        !hasRequiredLookupInput(address, currentDispensary)
      ) {
        return Promise.resolve('not_offered');
      }

      const activeRouter = router;
      const latitude = address.lat;
      const longitude = address.lng;
      const fingerprint = getStoreRoutingAttemptFingerprint(currentDispensary, address, Cart.isMedical);
      const isParticipantEligible = (): boolean => mountedRef.current && isStillEligible?.() !== false;
      if (activeAttempt?.fingerprint === fingerprint && activeAttempt.cancellation === null) {
        activeAttempt.participants.set(owner, isParticipantEligible);
        return activeAttempt.promise;
      }
      if (activeAttempt && activeAttempt.stage !== 'lookup') {
        return Promise.resolve('in_progress');
      }
      if (activeAttempt) {
        activeAttempt.cancellation = 'superseded';
      }

      const routeAtStart = activeRouter.asPath;
      const attempt: ActiveAttempt = {
        cancellation: null,
        fingerprint,
        participants: new Map([[owner, isParticipantEligible]]),
        promise: Promise.resolve('not_offered'),
        stage: 'lookup',
      };
      activeAttempt = attempt;

      attempt.promise = (async (): Promise<StoreRoutingOutcome> => {
        const isAttemptCurrent = (): boolean =>
          attempt.cancellation === null &&
          activeAttempt === attempt &&
          [...attempt.participants.values()].some((isEligible) => isEligible()) &&
          Boolean(flagsRef.current[STORE_ROUTING_FLAG]) &&
          activeRouter.asPath === routeAtStart;

        try {
          const queryResult = await queryCandidates({
            fetchPolicy: 'network-only',
            variables: {
              dispensaryFilter: {
                type: 'Dispensary',
                chain: currentDispensary.chain,
                nearLat: latitude,
                nearLng: longitude,
                deliveryAddress: {
                  ln1: address.ln1,
                  ln2: address.ln2,
                  city: address.city,
                  state: address.state,
                  country: address.country,
                  zipcode: address.zipcode,
                  geometry: { coordinates: [longitude, latitude] },
                },
                distance: 12500,
                deliversToAddress: true,
                openForDelivery: true,
                medical: Cart.isMedical,
                recreational: !Cart.isMedical,
                activeOnly: true,
                includeStealthMode: false,
              },
            },
          });

          if (!isAttemptCurrent()) {
            return attempt.cancellation ?? 'aborted';
          }

          const candidate = queryResult.data?.filteredDispensaries?.find(
            (location): location is StoreRoutingCandidate =>
              Boolean(location && location.id !== currentDispensary.id && location.name && location.cName)
          );
          if (queryResult.error || !candidate) {
            return 'not_offered';
          }

          const [href] = buildBaseRoute({
            isKiosk: UI.isKiosk,
            isEmbedded: UI.isEmbedded,
            isStoreFront: UI.isStoreFront,
            isMobileEcommApp: UI.isMobileEcommApp,
            dispensary: candidate,
          });
          const [destination] = addDispensaryRoutes({ href, route: href, dispensary: candidate });

          attempt.stage = 'modal';
          const transitionState = getStoreRoutingCartTransitionState(Cart, candidate.id);
          const modalResult = await awaitModalResult(
            openModal,
            {
              originStoreName: currentDispensary.name ?? '',
              destinationStoreName: candidate.name ?? '',
              address: formatAddress(address),
              willClearCart: transitionState.willClearCart,
            },
            activeRouter
          );

          if (modalResult === 'declined') {
            return 'declined';
          }
          if (modalResult === 'superseded') {
            return 'superseded';
          }
          if (!isAttemptCurrent()) {
            return attempt.cancellation ?? 'aborted';
          }

          const latestTransitionState = getStoreRoutingCartTransitionState(Cart, candidate.id);
          if (!transitionState.willClearCart && latestTransitionState.willClearCart) {
            return 'aborted';
          }

          attempt.stage = 'transition';

          clearCartForStoreRouting(Cart, candidate.id, address);
          UI.updateAddress({ location: address, residentialStatus: Cart.address.residentialStatus });
          Cart.setOrderType(ORDER_TYPE_DELIVERY);
          SearchPreferences.setOrderType(ORDER_TYPE_DELIVERY);

          try {
            const navigated = await activeRouter.push(destination);
            if (!navigated) {
              throw new Error('Store routing navigation returned false');
            }
            return 'accepted';
          } catch {
            if (!Cart.dispensary) {
              Cart.setDispensary(currentDispensary);
            }
            UI.showErnie(`We couldn't open ${candidate.name}. Please try again.`, 'danger');
            return 'navigation_failed';
          }
        } catch {
          if (attempt.stage === 'transition') {
            if (!Cart.dispensary) {
              Cart.setDispensary(currentDispensary);
            }
            UI.showErnie("We couldn't complete the store switch. Please review your cart and try again.", 'danger');
            return 'transition_failed';
          }
          return 'not_offered';
        } finally {
          if (activeAttempt === attempt) {
            activeAttempt = null;
          }
          attempt.participants.clear();
        }
      })();

      return attempt.promise;
    },
    [Cart, SearchPreferences, UI, openModal, owner, queryCandidates, router]
  );
}
